import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft, CheckCircle2, ArrowRight } from 'lucide-react';

const OtherDryFruits = () => {
  const products = [
    {
      id: 'dates',
      name: 'Dates',
      image: 'https://static.prod-images.emergentagent.com/jobs/cdb116b7-fdd7-4fa0-b46c-33572c5c834b/images/fcc878b838123b1687bfe7f3781e242cae66690908335b02d623f44826bc83b8.png',
      description: 'Premium Medjool and Deglet Noor dates for retail, gifting, and industrial applications.',
      highlights: [
        'Medjool, Deglet Noor, Khalas varieties',
        'Pitted and unpitted options',
        'Gift packaging available',
        'Year-round availability'
      ]
    },
    {
      id: 'apricots',
      name: 'Dried Apricots',
      image: 'https://static.prod-images.emergentagent.com/jobs/cdb116b7-fdd7-4fa0-b46c-33572c5c834b/images/29a95210895ee2d718c5c121eadad978a7bccf019939d0b883382b2a4618ab69.png',
      description: 'Turkish and Mediterranean sun-dried apricots for snacking, bakery, and confectionery.',
      highlights: [
        'Turkish Malatya apricots',
        'Sulphured and natural (organic) options',
        'Multiple sizes: Jumbo, Large, Standard',
        'Excellent for bakery applications'
      ]
    },
    {
      id: 'prunes',
      name: 'Prunes',
      image: 'https://static.prod-images.emergentagent.com/jobs/cdb116b7-fdd7-4fa0-b46c-33572c5c834b/images/eccdfa11a317b65522df4e761af63f20cbe7dc958fc316a441cf831f6c896817.png',
      description: 'Premium California and French prunes for retail and food manufacturing.',
      highlights: [
        'Pitted and unpitted varieties',
        'Multiple sizes available',
        'High moisture content for softness',
        'Perfect for health food applications'
      ]
    },
    {
      id: 'cranberries',
      name: 'Dried Cranberries',
      image: 'https://static.prod-images.emergentagent.com/jobs/cdb116b7-fdd7-4fa0-b46c-33572c5c834b/images/0a40cdc06fc41afd0dc12aae136784058c084f73b8f62d08575b11f9fb0ca9ce.png',
      description: 'Premium sweetened dried cranberries for bakery, cereal, and snack applications.',
      highlights: [
        'Sweetened and reduced-sugar options',
        'Whole and sliced varieties',
        'Ideal for trail mixes and cereals',
        'US and Canadian origins'
      ]
    },
    {
      id: 'hazelnuts',
      name: 'Hazelnuts',
      image: 'https://static.prod-images.emergentagent.com/jobs/cdb116b7-fdd7-4fa0-b46c-33572c5c834b/images/61b648283af910da619622f8decf33e805df76f885d298e1aa5d5f39c82224da.png',
      description: 'Premium Turkish hazelnuts for confectionery, chocolate, and praline manufacturing.',
      highlights: [
        'Blanched and natural kernels',
        'Roasted and raw options',
        'Ideal for Nutella-style spreads',
        'Multiple grades and sizes'
      ]
    },
    {
      id: 'macadamia',
      name: 'Macadamia Nuts',
      image: 'https://static.prod-images.emergentagent.com/jobs/cdb116b7-fdd7-4fa0-b46c-33572c5c834b/images/b641e39f28c9b769e5f90a8da9e6b03afcc59f07a9280ade08a547c44561947c.png',
      description: 'Premium macadamia nuts from Australia and Hawaii for gourmet applications.',
      highlights: [
        'Whole, halves, and pieces',
        'Raw and roasted options',
        'Premium packaging for retail',
        'Ideal for luxury confectionery'
      ]
    },
    {
      id: 'pecans',
      name: 'Pecans',
      image: 'https://static.prod-images.emergentagent.com/jobs/cdb116b7-fdd7-4fa0-b46c-33572c5c834b/images/e43407a6e8bcc7da6111ff1b28d3d03ea37023ef50d36a67d87e2bdef08b5952.png',
      description: 'Premium US pecans for bakery, confectionery, and snack applications.',
      highlights: [
        'Halves, pieces, and meal grades',
        'Light and amber color grades',
        'Ideal for pecan pies and pralines',
        'Consistent quality year-round'
      ]
    },
    {
      id: 'pine-nuts',
      name: 'Pine Nuts',
      image: 'https://static.prod-images.emergentagent.com/jobs/cdb116b7-fdd7-4fa0-b46c-33572c5c834b/images/2028186e23ad3f683ade6c02164a9c4bacccc7babcfb538f5a502872205ceebc.png',
      description: 'Premium Mediterranean and Chinese pine nuts for culinary and pesto applications.',
      highlights: [
        'Mediterranean and Chinese origins',
        'Ideal for pesto and Italian cuisine',
        'Carefully selected for taste',
        'Retail and bulk packaging'
      ]
    }
  ];

  return (
    <div className="w-full">
      {/* Hero */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Link to="/products/dry-fruits" className="inline-flex items-center text-[#263776] hover:text-[#F47D20] mb-6">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dry Fruits
            </Link>
            <div className="text-center">
              <h1 className="text-5xl font-bold mb-4">Other Dry Fruits & Nuts</h1>
              <p className="text-xl text-slate-700">
                Explore our extended range of premium dry fruits and nuts for diverse applications
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {products.map((product) => (
                <Card key={product.id} className="overflow-hidden hover:shadow-xl transition-shadow">
                  <div className="h-48 overflow-hidden">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-[#263776] mb-2">{product.name}</h3>
                    <p className="text-slate-600 text-sm mb-4">{product.description}</p>
                    <ul className="space-y-2 mb-4">
                      {product.highlights.slice(0, 3).map((highlight, idx) => (
                        <li key={idx} className="flex items-start text-sm">
                          <CheckCircle2 className="w-4 h-4 text-[#F47D20] mr-2 flex-shrink-0 mt-0.5" />
                          <span className="text-slate-600">{highlight}</span>
                        </li>
                      ))}
                    </ul>
                    <Button asChild className="w-full bg-[#263776] text-white hover:bg-[#1e2a5e]">
                      <Link to="/contact">
                        Inquire Now
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Link>
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Custom Sourcing */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6 text-[#263776]">Need Something Specific?</h2>
            <p className="text-lg text-slate-700 mb-8">
              Our sourcing network spans multiple origins worldwide. If you're looking for a specific dry fruit 
              or nut variety not listed here, our team can help you source it with the same quality standards 
              and documentation you expect from SPANXPORT.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <Card className="p-6 text-center">
                <h3 className="font-semibold text-lg mb-2 text-[#263776]">Custom Sourcing</h3>
                <p className="text-sm text-slate-600">Tell us your requirements and we'll find the right supplier</p>
              </Card>
              <Card className="p-6 text-center">
                <h3 className="font-semibold text-lg mb-2 text-[#263776]">Quality Assurance</h3>
                <p className="text-sm text-slate-600">Every product meets our stringent quality standards</p>
              </Card>
              <Card className="p-6 text-center">
                <h3 className="font-semibold text-lg mb-2 text-[#263776]">Documentation</h3>
                <p className="text-sm text-slate-600">Complete COA and traceability for every shipment</p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#263776] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Source Premium Dry Fruits?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Share your requirements—product type, grade, quantity, and destination. We'll provide a detailed quote 
            with complete documentation checklist.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Button asChild size="lg" className="bg-[#F47D20] text-white hover:bg-[#d66b1a]">
              <Link to="/contact">Request a Quote</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white bg-white/10 hover:bg-white/20">
              <Link to="/products/dry-fruits">View Main Dry Fruits</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OtherDryFruits;
